/**
 * Phase 5S.A — Recruitment & Onboarding (Foundation).
 *
 * Single-page admin module with tabs for Dashboard, Requisitions,
 * Job Profiles, Applications, Blacklist, and Masters. Lives under
 * /admin/recruitment.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PipelineDrawer from "@/components/PipelineDrawer";
import {
  Briefcase, FileText, Users, ShieldAlert, Settings,
  CheckCircle2, XCircle, Clock, Plus, BarChart3,
  AlertTriangle, ExternalLink, TrendingUp, Zap,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Cell, LabelList,
} from "recharts";

const STAGE_LABELS = {
  new: "New", cv_screen: "CV Screen", telephonic: "Telephonic",
  assessment: "Assessment", interview: "Interview", final: "Final",
  offer: "Offer", bgv: "BGV", joining: "Joining", joined: "Joined",
  rejected: "Rejected", declined: "Declined",
};
const STAGE_PALETTE = {
  new: "bg-slate-100 text-slate-700",
  cv_screen: "bg-blue-100 text-blue-800",
  telephonic: "bg-cyan-100 text-cyan-800",
  assessment: "bg-purple-100 text-purple-800",
  interview: "bg-indigo-100 text-indigo-800",
  final: "bg-violet-100 text-violet-800",
  offer: "bg-amber-100 text-amber-800",
  bgv: "bg-orange-100 text-orange-800",
  joining: "bg-red-100 text-red-800",
  joined: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  declined: "bg-red-100 text-red-800",
};
const REQ_STATUS_PALETTE = {
  pending_hod: "bg-amber-100 text-amber-800",
  pending_senior_mgmt: "bg-orange-100 text-orange-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  hold: "bg-slate-200 text-slate-700",
  closed: "bg-slate-100 text-slate-500",
};

export default function Recruitment() {
  const { user } = useAuth();
  const role = user?.role;
  const canManageMasters = ["super_admin", "hr"].includes(role);

  const [tab, setTab] = useState("dashboard");
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [masters, setMasters] = useState({}); // {sources: [...], ...}

  // Load shared deps
  useEffect(() => {
    Promise.all([
      api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {}),
      api.get("/departments").then((r) => setDepartments(r.data || [])).catch(() => {}),
      ...["sources", "rejection_reasons_by_us", "rejection_reasons_by_candidate",
           "interview_types", "document_checklist_items"].map((k) =>
        api.get(`/recruitment/masters/${k}`)
          .then((r) => setMasters((p) => ({ ...p, [k]: r.data || [] })))
          .catch(() => {}),
      ),
    ]);
  }, []);

  return (
    <div className="space-y-4" data-testid="recruitment-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Briefcase className="h-3 w-3" /> Phase A — Foundation
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Chivo" }}>
          Recruitment &amp; Onboarding
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Manpower requisitions, job profiles, candidate applications and blacklist.
          Communications + interview scheduling coming in next phase.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-3.5 w-3.5 mr-1" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="sm-view" data-testid="tab-sm-view">
            <BarChart3 className="h-3.5 w-3.5 mr-1" /> SM View
          </TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">
            <Users className="h-3.5 w-3.5 mr-1" /> Live Pipeline
          </TabsTrigger>
          <TabsTrigger value="requisitions" data-testid="tab-requisitions">
            <FileText className="h-3.5 w-3.5 mr-1" /> Requisitions
          </TabsTrigger>
          <TabsTrigger value="profiles" data-testid="tab-profiles">
            <Briefcase className="h-3.5 w-3.5 mr-1" /> Job Profiles
          </TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications">
            <Users className="h-3.5 w-3.5 mr-1" /> Applications
          </TabsTrigger>
          <TabsTrigger value="blacklist" data-testid="tab-blacklist">
            <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Blacklist
          </TabsTrigger>
          {canManageMasters && (
            <TabsTrigger value="masters" data-testid="tab-masters">
              <Settings className="h-3.5 w-3.5 mr-1" /> Masters
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="dashboard" className="mt-4"><DashboardTab /></TabsContent>
        <TabsContent value="sm-view" className="mt-4"><SMViewTab role={role} setTab={setTab} /></TabsContent>
        <TabsContent value="pipeline" className="mt-4"><LivePipelineTab /></TabsContent>
        <TabsContent value="requisitions" className="mt-4">
          <RequisitionsTab units={units} departments={departments} role={role} />
        </TabsContent>
        <TabsContent value="profiles" className="mt-4">
          <JobProfilesTab departments={departments} />
        </TabsContent>
        <TabsContent value="applications" className="mt-4">
          <ApplicationsTab masters={masters} />
        </TabsContent>
        <TabsContent value="blacklist" className="mt-4">
          <BlacklistTab canEdit={canManageMasters} />
        </TabsContent>
        {canManageMasters && (
          <TabsContent value="masters" className="mt-4">
            <MastersTab masters={masters} reload={(k) =>
              api.get(`/recruitment/masters/${k}`).then((r) =>
                setMasters((p) => ({ ...p, [k]: r.data || [] }))).catch(() => {})
            } />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ===================== Dashboard Tab ===================== */
function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/recruitment/dashboard").then(({ data }) => setData(data));
  }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Briefcase className="h-3 w-3" /> Open Positions</div>
          <div className="text-2xl font-bold mt-1 brand-text tabular-nums" data-testid="kpi-open">{data.kpis.open_positions}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Pending Requisitions</div>
          <div className="text-2xl font-bold mt-1 text-amber-700 tabular-nums" data-testid="kpi-pending">{data.kpis.pending_requisitions}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" /> Total Applications</div>
          <div className="text-2xl font-bold mt-1 text-slate-900 tabular-nums" data-testid="kpi-apps">{data.kpis.total_applications}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Joined</div>
          <div className="text-2xl font-bold mt-1 text-emerald-700 tabular-nums" data-testid="kpi-joined">{data.kpis.joined}</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Pipeline funnel</div>
          <div className="flex gap-1.5 flex-wrap">
            {Object.entries(data.funnel).map(([stage, count]) => (
              <div key={stage} className={`px-2.5 py-1.5 rounded ${STAGE_PALETTE[stage] || "bg-slate-100"}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-80">{STAGE_LABELS[stage] || stage}</div>
                <div className="text-lg font-semibold tabular-nums">{count}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 border-b">Recent applications</div>
          <table className="w-full text-sm">
            <tbody>
              {data.recent_applications.length === 0 && (
                <tr><td className="px-3 py-6 text-center text-slate-400 text-xs">No applications yet</td></tr>
              )}
              {data.recent_applications.map((a) => (
                <tr key={a.id} className="border-b last:border-b-0 border-slate-100">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{a.name}</div>
                    <div className="text-[10px] text-slate-500">{a.mobile} · {a.source}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{a.opening_label || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${STAGE_PALETTE[a.stage] || "bg-slate-100"}`}>
                      {STAGE_LABELS[a.stage] || a.stage}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ===================== Requisitions Tab ===================== */
function RequisitionsTab({ units, departments, role }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    department_id: "", unit_id: "", designation: "", openings: 1,
    budgeted_ctc_min: "", budgeted_ctc_max: "", priority: "medium",
    target_joining_date: "", requisition_type: "new", reason: "",
    remarks: "",
  });
  const [decisionDlg, setDecisionDlg] = useState(null);
  const [decisionForm, setDecisionForm] = useState({ decision: "approve", remarks: "" });
  const [editDlg, setEditDlg] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [closeDlg, setCloseDlg] = useState(null);
  const [closeForm, setCloseForm] = useState({ reason: "filled", remarks: "" });
  const [statusFilter, setStatusFilter] = useState("all");
  const canDecide = ["super_admin", "hr", "hr_admin", "senior_management", "hod"].includes(role);
  const canEdit = ["super_admin", "hr", "hr_admin", "senior_management", "hod"].includes(role);
  const canClose = ["super_admin", "hr", "hr_admin", "senior_management"].includes(role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recruitment/requisitions");
      setRows(data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const PENDING_STAGES = ["pending_hod", "pending_senior_mgmt", "hold"];
  const visibleRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    if (statusFilter === "pending") return rows.filter((r) => PENDING_STAGES.includes(r.status));
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);
  const statusCounts = useMemo(() => ({
    all: rows.length,
    pending: rows.filter((r) => PENDING_STAGES.includes(r.status)).length,
    approved: rows.filter((r) => r.status === "approved").length,
    closed: rows.filter((r) => r.status === "closed").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
  }), [rows]);

  const submit = async () => {
    if (!form.department_id || !form.unit_id || !form.designation) {
      toast.error("Department, Unit, Designation required"); return;
    }
    try {
      const payload = {
        ...form,
        openings: parseInt(form.openings) || 1,
        budgeted_ctc_min: form.budgeted_ctc_min ? parseFloat(form.budgeted_ctc_min) : null,
        budgeted_ctc_max: form.budgeted_ctc_max ? parseFloat(form.budgeted_ctc_max) : null,
        target_joining_date: form.target_joining_date || null,
        reason: form.reason || null,
      };
      await api.post("/recruitment/requisitions", payload);
      toast.success("Requisition raised");
      setOpen(false);
      setForm({ department_id: "", unit_id: "", designation: "", openings: 1,
                budgeted_ctc_min: "", budgeted_ctc_max: "", priority: "medium",
                target_joining_date: "", requisition_type: "new", reason: "", remarks: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const decide = async () => {
    if (!decisionDlg) return;
    try {
      await api.post(`/recruitment/requisitions/${decisionDlg.id}/decision`, decisionForm);
      toast.success(`Requisition ${decisionForm.decision}d`);
      setDecisionDlg(null);
      setDecisionForm({ decision: "approve", remarks: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const openEdit = (r) => {
    setEditForm({
      designation: r.designation || "",
      openings: r.openings || 1,
      budgeted_ctc_min: r.budgeted_ctc_min ?? "",
      budgeted_ctc_max: r.budgeted_ctc_max ?? "",
      priority: r.priority || "medium",
      target_joining_date: r.target_joining_date || "",
      remarks: r.remarks || "",
    });
    setEditDlg(r);
  };

  const saveEdit = async () => {
    if (!editDlg) return;
    try {
      const payload = {
        ...editForm,
        openings: parseInt(editForm.openings) || 1,
        budgeted_ctc_min: editForm.budgeted_ctc_min === "" ? null : parseFloat(editForm.budgeted_ctc_min),
        budgeted_ctc_max: editForm.budgeted_ctc_max === "" ? null : parseFloat(editForm.budgeted_ctc_max),
        target_joining_date: editForm.target_joining_date || null,
      };
      await api.patch(`/recruitment/requisitions/${editDlg.id}`, payload);
      toast.success("Requisition updated");
      setEditDlg(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };

  const submitClose = async () => {
    if (!closeDlg) return;
    try {
      await api.post(`/recruitment/requisitions/${closeDlg.id}/close`, closeForm);
      toast.success("Position closed");
      setCloseDlg(null);
      setCloseForm({ reason: "filled", remarks: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to close");
    }
  };

  const reopenReq = async (r) => {
    if (!window.confirm(`Reopen "${r.designation}" (${r.opening_id})? Status returns to Approved.`)) return;
    try {
      await api.post(`/recruitment/requisitions/${r.id}/reopen`);
      toast.success("Position reopened");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reopen");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="text-sm text-slate-600">{visibleRows.length} requisitions</div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="req-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses ({statusCounts.all})</SelectItem>
              <SelectItem value="pending">Pending ({statusCounts.pending})</SelectItem>
              <SelectItem value="approved">Approved ({statusCounts.approved})</SelectItem>
              <SelectItem value="closed">Closed ({statusCounts.closed})</SelectItem>
              <SelectItem value="rejected">Rejected ({statusCounts.rejected})</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setOpen(true)} className="brand-bg brand-bg-hover text-white" data-testid="new-req-btn">
          <Plus className="h-4 w-4 mr-1" /> New Requisition
        </Button>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Opening ID</th>
                <th className="px-3 py-2 text-left">Designation</th>
                <th className="px-3 py-2 text-left">Dept · Unit</th>
                <th className="px-3 py-2 text-right">Openings</th>
                <th className="px-3 py-2 text-right">Budgeted CTC</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && visibleRows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No requisitions match this filter</td></tr>}
              {visibleRows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`req-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.opening_id}</td>
                  <td className="px-3 py-2 font-medium">{r.designation}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.department_name} · {r.unit_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.openings}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">
                    {r.budgeted_ctc_min ? `₹${(r.budgeted_ctc_min / 1000).toFixed(0)}k` : "—"}
                    {r.budgeted_ctc_max ? ` – ₹${(r.budgeted_ctc_max / 1000).toFixed(0)}k` : ""}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${REQ_STATUS_PALETTE[r.status] || "bg-slate-100"}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      {canDecide && ["pending_hod", "pending_senior_mgmt", "hold"].includes(r.status) && (
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => setDecisionDlg(r)} data-testid={`decide-${r.id}`}>
                          Decide
                        </Button>
                      )}
                      {canEdit && !["closed", "rejected"].includes(r.status) && (
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => openEdit(r)} data-testid={`edit-req-${r.id}`}>
                          Edit
                        </Button>
                      )}
                      {canClose && r.status === "approved" && (
                        <Button size="sm" variant="outline"
                          className="h-7 text-xs text-slate-600 border-slate-300 hover:bg-slate-100"
                          onClick={() => setCloseDlg(r)} data-testid={`close-req-${r.id}`}>
                          Close
                        </Button>
                      )}
                      {canClose && r.status === "closed" && (
                        <Button size="sm" variant="outline"
                          className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          onClick={() => reopenReq(r)} data-testid={`reopen-req-${r.id}`}>
                          Reopen
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="new-req-dialog">
          <DialogHeader>
            <DialogTitle>Raise Manpower Requisition</DialogTitle>
            <DialogDescription className="sr-only">Create a new headcount request for approval.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Department *</Label>
              <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="req-dept"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Unit *</Label>
              <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="req-unit"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Designation *</Label>
              <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}
                placeholder="e.g. Production Helper" className="h-8 mt-1 text-xs" data-testid="req-designation" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">No. of Openings</Label>
              <Input type="number" min="1" value={form.openings}
                onChange={(e) => setForm({ ...form, openings: e.target.value })}
                className="h-8 mt-1 text-xs" data-testid="req-openings" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Budgeted CTC Min (₹)</Label>
              <Input type="number" value={form.budgeted_ctc_min}
                onChange={(e) => setForm({ ...form, budgeted_ctc_min: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Budgeted CTC Max (₹)</Label>
              <Input type="number" value={form.budgeted_ctc_max}
                onChange={(e) => setForm({ ...form, budgeted_ctc_max: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Type</Label>
              <Select value={form.requisition_type} onValueChange={(v) => setForm({ ...form, requisition_type: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="replacement">Replacement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Target Joining Date</Label>
              <Input type="date" value={form.target_joining_date}
                onChange={(e) => setForm({ ...form, target_joining_date: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Remarks</Label>
              <Textarea rows={2} value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                className="mt-1 text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="brand-bg brand-bg-hover text-white" data-testid="submit-req-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!decisionDlg} onOpenChange={(v) => !v && setDecisionDlg(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="decide-req-dialog">
          <DialogHeader>
            <DialogTitle>Decide on Requisition</DialogTitle>
            <DialogDescription className="sr-only">Approve, hold or reject the requisition.</DialogDescription>
          </DialogHeader>
          {decisionDlg && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 border rounded p-2 text-xs">
                <div className="font-mono">{decisionDlg.opening_id}</div>
                <div className="font-medium text-slate-900 mt-1">{decisionDlg.designation}</div>
                <div className="text-slate-500">{decisionDlg.department_name} · {decisionDlg.unit_name} · {decisionDlg.openings} openings</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Decision</Label>
                <Select value={decisionForm.decision} onValueChange={(v) => setDecisionForm({ ...decisionForm, decision: v })}>
                  <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approve">Approve</SelectItem>
                    <SelectItem value="hold">Put on Hold</SelectItem>
                    <SelectItem value="reject">Reject</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Remarks</Label>
                <Textarea rows={2} value={decisionForm.remarks}
                  onChange={(e) => setDecisionForm({ ...decisionForm, remarks: e.target.value })}
                  className="mt-1 text-xs" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDlg(null)}>Cancel</Button>
            <Button onClick={decide} className="brand-bg brand-bg-hover text-white" data-testid="submit-decision-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editDlg} onOpenChange={(v) => !v && setEditDlg(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="edit-req-dialog">
          <DialogHeader>
            <DialogTitle>Edit Requisition</DialogTitle>
            <DialogDescription className="sr-only">Update requisition details.</DialogDescription>
          </DialogHeader>
          {editDlg && (
            <>
              <div className="bg-slate-50 border rounded p-2 text-xs mb-1">
                <div className="font-mono">{editDlg.opening_id}</div>
                <div className="text-slate-500">{editDlg.department_name} · {editDlg.unit_name} (fixed)</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">Designation *</Label>
                  <Input value={editForm.designation}
                    onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })}
                    className="h-8 mt-1 text-xs" data-testid="edit-req-designation" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">No. of Openings</Label>
                  <Input type="number" min="1" value={editForm.openings}
                    onChange={(e) => setEditForm({ ...editForm, openings: e.target.value })}
                    className="h-8 mt-1 text-xs" data-testid="edit-req-openings" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Priority</Label>
                  <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                    <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Budgeted CTC Min (₹)</Label>
                  <Input type="number" value={editForm.budgeted_ctc_min}
                    onChange={(e) => setEditForm({ ...editForm, budgeted_ctc_min: e.target.value })}
                    className="h-8 mt-1 text-xs" data-testid="edit-req-ctc-min" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Budgeted CTC Max (₹)</Label>
                  <Input type="number" value={editForm.budgeted_ctc_max}
                    onChange={(e) => setEditForm({ ...editForm, budgeted_ctc_max: e.target.value })}
                    className="h-8 mt-1 text-xs" data-testid="edit-req-ctc-max" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Target Joining Date</Label>
                  <Input type="date" value={editForm.target_joining_date}
                    onChange={(e) => setEditForm({ ...editForm, target_joining_date: e.target.value })}
                    className="h-8 mt-1 text-xs" data-testid="edit-req-target-date" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">Remarks</Label>
                  <Textarea rows={2} value={editForm.remarks}
                    onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                    className="mt-1 text-xs" data-testid="edit-req-remarks" />
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="brand-bg brand-bg-hover text-white" data-testid="submit-edit-req-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closeDlg} onOpenChange={(v) => !v && setCloseDlg(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="close-req-dialog">
          <DialogHeader>
            <DialogTitle>Close Position</DialogTitle>
            <DialogDescription className="sr-only">Mark this requisition as closed.</DialogDescription>
          </DialogHeader>
          {closeDlg && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 border rounded p-2 text-xs">
                <div className="font-mono">{closeDlg.opening_id}</div>
                <div className="font-medium text-slate-900 mt-1">{closeDlg.designation}</div>
                <div className="text-slate-500">{closeDlg.department_name} · {closeDlg.unit_name} · {closeDlg.openings} openings</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Reason *</Label>
                <Select value={closeForm.reason} onValueChange={(v) => setCloseForm({ ...closeForm, reason: v })}>
                  <SelectTrigger className="h-8 mt-1 text-xs" data-testid="close-req-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="filled">Filled</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="budget_withdrawn">Budget Withdrawn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Remarks</Label>
                <Textarea rows={2} value={closeForm.remarks}
                  onChange={(e) => setCloseForm({ ...closeForm, remarks: e.target.value })}
                  className="mt-1 text-xs" data-testid="close-req-remarks" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDlg(null)}>Cancel</Button>
            <Button onClick={submitClose} className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="submit-close-req-btn">Close Position</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===================== Job Profiles Tab ===================== */
function JobProfilesTab({ departments }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    job_title: "", department_id: "", reporting_to: "",
    key_responsibilities: "", required_skills: "", experience: "",
    education: "", salary_range_min: "", salary_range_max: "", is_active: true,
  });

  const load = useCallback(() => {
    api.get("/recruitment/job-profiles").then(({ data }) => setRows(data || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.job_title || !form.department_id) {
      toast.error("Title + Department required"); return;
    }
    try {
      await api.post("/recruitment/job-profiles", {
        ...form,
        salary_range_min: form.salary_range_min ? parseFloat(form.salary_range_min) : null,
        salary_range_max: form.salary_range_max ? parseFloat(form.salary_range_max) : null,
      });
      toast.success("Profile created");
      setOpen(false);
      setForm({ job_title: "", department_id: "", reporting_to: "", key_responsibilities: "",
                required_skills: "", experience: "", education: "",
                salary_range_min: "", salary_range_max: "", is_active: true });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <div className="text-sm text-slate-600">{rows.length} active profiles</div>
        <Button onClick={() => setOpen(true)} className="brand-bg brand-bg-hover text-white" data-testid="new-profile-btn">
          <Plus className="h-4 w-4 mr-1" /> New Profile
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <Card key={r.id} data-testid={`profile-${r.id}`}>
            <CardContent className="p-3">
              <div className="font-semibold text-slate-900">{r.job_title}</div>
              <div className="text-xs text-slate-500">{r.department_name} · Reports to {r.reporting_to || "—"}</div>
              {r.experience && <div className="text-[11px] text-slate-600 mt-1">{r.experience}</div>}
              {(r.salary_range_min || r.salary_range_max) && (
                <div className="text-[11px] text-slate-600">
                  ₹{r.salary_range_min ? (r.salary_range_min / 1000).toFixed(0) + "k" : "?"}
                  {" – "}
                  ₹{r.salary_range_max ? (r.salary_range_max / 1000).toFixed(0) + "k" : "?"}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {rows.length === 0 && (
          <div className="col-span-2 text-center text-slate-400 text-sm py-8">No job profiles yet</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle>New Job Profile</DialogTitle>
            <DialogDescription className="sr-only">Define a reusable job profile for openings.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Job Title *</Label>
              <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                className="h-8 mt-1 text-xs" data-testid="profile-title" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Department *</Label>
              <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Reporting To</Label>
              <Input value={form.reporting_to} onChange={(e) => setForm({ ...form, reporting_to: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Responsibilities</Label>
              <Textarea rows={2} value={form.key_responsibilities}
                onChange={(e) => setForm({ ...form, key_responsibilities: e.target.value })}
                className="mt-1 text-xs" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Required Skills</Label>
              <Textarea rows={2} value={form.required_skills}
                onChange={(e) => setForm({ ...form, required_skills: e.target.value })}
                className="mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Experience</Label>
              <Input value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })}
                placeholder="e.g. 2-5 years" className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Education</Label>
              <Input value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Salary Min (₹)</Label>
              <Input type="number" value={form.salary_range_min}
                onChange={(e) => setForm({ ...form, salary_range_min: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Salary Max (₹)</Label>
              <Input type="number" value={form.salary_range_max}
                onChange={(e) => setForm({ ...form, salary_range_max: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="brand-bg brand-bg-hover text-white" data-testid="submit-profile-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===================== Applications Tab ===================== */
function ApplicationsTab({ masters }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [reqs, setReqs] = useState([]);
  const [drawerApp, setDrawerApp] = useState(null);
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "", source: "", source_details: "",
    opening_id: "", current_company: "", current_ctc: "", expected_ctc: "",
    notice_period: "", experience_years: "", remarks: "",
  });
  const [dupCheck, setDupCheck] = useState(null);

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (stageFilter !== "all") q.set("stage", stageFilter);
    const { data } = await api.get(`/recruitment/applications?${q}`);
    setRows(data || []);
  }, [search, stageFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/recruitment/requisitions?status=approved").then(({ data }) => setReqs(data || []));
  }, []);

  const checkMobile = async (mob) => {
    if (!mob || mob.length < 10) { setDupCheck(null); return; }
    try {
      const { data } = await api.get(`/recruitment/applications/check-mobile/${encodeURIComponent(mob)}`);
      setDupCheck(data);
    } catch { setDupCheck(null); }
  };

  const submit = async () => {
    if (!form.name || !form.mobile || !form.source) {
      toast.error("Name, mobile and source required"); return;
    }
    try {
      await api.post("/recruitment/applications", {
        ...form,
        current_ctc: form.current_ctc ? parseFloat(form.current_ctc) : null,
        expected_ctc: form.expected_ctc ? parseFloat(form.expected_ctc) : null,
        experience_years: form.experience_years ? parseFloat(form.experience_years) : null,
        opening_id: form.opening_id || null,
      });
      toast.success("Application created");
      setOpen(false);
      setForm({ name: "", mobile: "", email: "", city: "", source: "", source_details: "",
                opening_id: "", current_company: "", current_ctc: "", expected_ctc: "",
                notice_period: "", experience_years: "", remarks: "" });
      setDupCheck(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / mobile / city…" className="h-8 w-64 text-xs" data-testid="app-search" />
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {Object.entries(STAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto" />
        <Button onClick={() => setOpen(true)} className="brand-bg brand-bg-hover text-white" data-testid="new-app-btn">
          <Plus className="h-4 w-4 mr-1" /> New Application
        </Button>
      </div>
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Name · Mobile</th>
              <th className="px-3 py-2 text-left">Opening</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-right">Stage</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">No applications</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-red-50/30 cursor-pointer"
                onClick={() => setDrawerApp(a)}
                data-testid={`app-row-${a.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{a.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {a.mobile}
                    {a.duplicate_flag && <span className="ml-1 text-amber-700 bg-amber-100 px-1 rounded text-[9px]">DUP</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">{a.opening_label || "—"}</td>
                <td className="px-3 py-2 text-xs">{a.source}</td>
                <td className="px-3 py-2 text-right">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${STAGE_PALETTE[a.stage] || "bg-slate-100"}`}>
                    {STAGE_LABELS[a.stage] || a.stage}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="new-app-dialog">
          <DialogHeader>
            <DialogTitle>New Application</DialogTitle>
            <DialogDescription className="sr-only">Add a candidate to the application tracker.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-8 mt-1 text-xs" data-testid="app-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Mobile *</Label>
              <Input value={form.mobile}
                onChange={(e) => { setForm({ ...form, mobile: e.target.value }); checkMobile(e.target.value); }}
                className="h-8 mt-1 text-xs" data-testid="app-mobile" />
              {dupCheck && (dupCheck.blacklisted || dupCheck.duplicate) && (
                <div className={`text-[10px] mt-1 px-1.5 py-0.5 rounded ${dupCheck.blacklisted ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                  {dupCheck.blacklisted
                    ? `⚠ Do-Not-Hire: ${dupCheck.blacklist_reason}`
                    : `⚠ Duplicate — existing applicant ${dupCheck.existing?.name || ""}`}
                </div>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Source *</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="app-source"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(masters.sources || []).map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Source Details</Label>
              <Input value={form.source_details} onChange={(e) => setForm({ ...form, source_details: e.target.value })}
                placeholder="Consultant name etc." className="h-8 mt-1 text-xs" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Opening</Label>
              <Select value={form.opening_id} onValueChange={(v) => setForm({ ...form, opening_id: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue placeholder="(optional) link to opening" /></SelectTrigger>
                <SelectContent>{reqs.map((r) => <SelectItem key={r.id} value={r.id}>{r.opening_id} — {r.designation}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Current Company</Label>
              <Input value={form.current_company} onChange={(e) => setForm({ ...form, current_company: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Notice Period</Label>
              <Input value={form.notice_period} onChange={(e) => setForm({ ...form, notice_period: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Current CTC (₹)</Label>
              <Input type="number" value={form.current_ctc} onChange={(e) => setForm({ ...form, current_ctc: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Expected CTC (₹)</Label>
              <Input type="number" value={form.expected_ctc} onChange={(e) => setForm({ ...form, expected_ctc: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Experience (yrs)</Label>
              <Input type="number" step="0.5" value={form.experience_years}
                onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                className="h-8 mt-1 text-xs" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Remarks</Label>
              <Textarea rows={2} value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                className="mt-1 text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={dupCheck?.blacklisted}
              className="brand-bg brand-bg-hover text-white" data-testid="submit-app-btn">
              {dupCheck?.blacklisted ? "Blacklisted — cannot submit" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {drawerApp && (
        <PipelineDrawer
          application={drawerApp}
          onClose={() => setDrawerApp(null)}
          onUpdated={load}
        />
      )}
    </div>
  );
}

/* ===================== Blacklist Tab ===================== */
function BlacklistTab({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", reason: "" });
  const load = useCallback(() => {
    api.get("/recruitment/blacklist").then(({ data }) => setRows(data || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.name || !form.mobile || !form.reason) { toast.error("All fields required"); return; }
    try {
      await api.post("/recruitment/blacklist", form);
      toast.success("Added to Do-Not-Hire");
      setOpen(false);
      setForm({ name: "", mobile: "", reason: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const removeBl = async (id) => {
    if (!window.confirm("Remove from Do-Not-Hire list?")) return;
    try {
      await api.delete(`/recruitment/blacklist/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <div className="text-sm text-slate-600 flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5 text-red-600" /> {rows.length} on Do-Not-Hire
        </div>
        {canEdit && (
          <Button onClick={() => setOpen(true)} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="new-bl-btn">
            <Plus className="h-4 w-4 mr-1" /> Add to Blacklist
          </Button>
        )}
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Mobile</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Added By</th>
              {canEdit && <th className="px-3 py-2 text-right">Action</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={canEdit ? 5 : 4} className="px-3 py-8 text-center text-slate-400">No entries</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`bl-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.mobile}</td>
                <td className="px-3 py-2 text-xs text-slate-700">{r.reason}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.added_by_name}</td>
                {canEdit && <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => removeBl(r.id)} className="h-6 px-2 text-red-600">
                    <XCircle className="h-3 w-3" />
                  </Button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="new-bl-dialog">
          <DialogHeader>
            <DialogTitle>Add to Do-Not-Hire</DialogTitle>
            <DialogDescription className="sr-only">Permanently block this mobile number from future applications.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-8 mt-1 text-xs" data-testid="bl-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Mobile *</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                className="h-8 mt-1 text-xs" data-testid="bl-mobile" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Reason *</Label>
              <Textarea rows={2} value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Forged documents, theft, repeated no-shows..."
                className="mt-1 text-xs" data-testid="bl-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-red-600 hover:bg-red-700 text-white" data-testid="submit-bl-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===================== Masters Tab ===================== */
function MastersTab({ masters, reload }) {
  const KINDS = [
    { k: "sources", label: "Job Sources" },
    { k: "rejection_reasons_by_us", label: "Rejection Reasons (By Us)" },
    { k: "rejection_reasons_by_candidate", label: "Rejection Reasons (By Candidate)" },
    { k: "interview_types", label: "Interview Types" },
    { k: "document_checklist_items", label: "Document Checklist" },
  ];
  const [add, setAdd] = useState({});

  const handleAdd = async (k) => {
    const name = (add[k] || "").trim();
    if (!name) return;
    try {
      await api.post(`/recruitment/masters/${k}`, { name });
      toast.success("Added");
      setAdd({ ...add, [k]: "" });
      reload(k);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const handleRemove = async (k, id) => {
    if (!window.confirm("Deactivate this entry?")) return;
    try {
      await api.delete(`/recruitment/masters/${id}`);
      toast.success("Removed");
      reload(k);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {KINDS.map(({ k, label }) => (
        <Card key={k}>
          <CardContent className="p-3" data-testid={`master-${k}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{label}</div>
            <div className="flex flex-wrap gap-1 mb-2">
              {(masters[k] || []).map((m) => (
                <span key={m.id} className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 px-2 py-0.5 rounded text-xs" data-testid={`m-${k}-${m.id}`}>
                  {m.name}
                  <button onClick={() => handleRemove(k, m.id)} className="text-slate-400 hover:text-red-600 ml-0.5">×</button>
                </span>
              ))}
              {(masters[k] || []).length === 0 && <span className="text-xs text-slate-400">Empty</span>}
            </div>
            <div className="flex gap-1">
              <Input value={add[k] || ""} onChange={(e) => setAdd({ ...add, [k]: e.target.value })}
                placeholder="Add new…" className="h-7 text-xs"
                onKeyDown={(e) => e.key === "Enter" && handleAdd(k)} />
              <Button size="sm" onClick={() => handleAdd(k)} className="h-7 text-xs brand-bg brand-bg-hover text-white">Add</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


/* ===================== Phase 16.3 — SM View Tab ===================== */
function SMViewTab({ role, setTab }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [decisionDlg, setDecisionDlg] = useState(null);
  const [decisionForm, setDecisionForm] = useState({ decision: "approve", remarks: "" });
  const isSM = ["senior_management", "super_admin", "hr"].includes(role);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/recruitment/sm-dashboard");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async () => {
    if (!decisionDlg) return;
    if (decisionForm.decision === "reject" && (decisionForm.remarks || "").trim().length < 5) {
      toast.error("Reason required (min 5 chars)"); return;
    }
    try {
      await api.post(`/recruitment/requisitions/${decisionDlg.id}/decision`, decisionForm);
      toast.success(`Requisition ${decisionForm.decision}d`);
      setDecisionDlg(null);
      setDecisionForm({ decision: "approve", remarks: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  if (busy && !data) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!data) return null;

  const t = data.tiles || {};
  const tile = (label, value, sub, tone) => (
    <Card><CardContent className="p-3">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </CardContent></Card>
  );

  return (
    <div className="space-y-4" data-testid="sm-view-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tile("Open Positions", t.open_positions ?? 0, null, "brand-text")}
        {tile("Pending SM Approval", t.pending_sm_approval ?? 0,
              isSM && t.pending_sm_approval > 0 ? "Action needed" : null,
              t.pending_sm_approval > 0 ? "text-red-700" : "text-slate-700")}
        {tile("Pending HOD Approval", t.pending_hod_approval ?? 0, null, "text-amber-700")}
        {tile("Interviews (this week)", t.interviews_this_week ?? 0, null, "text-blue-700")}
        {tile("Joined (this month)", t.joined_this_month ?? 0, null, "text-emerald-700")}
      </div>

      {isSM && (data.awaiting_me || []).length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                Awaiting My Approval
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                  {data.awaiting_me.length}
                </span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1.5">Req ID</th>
                    <th className="text-left px-2 py-1.5">Designation</th>
                    <th className="text-right px-2 py-1.5">Openings</th>
                    <th className="text-right px-2 py-1.5">CTC (₹)</th>
                    <th className="text-left px-2 py-1.5">Priority</th>
                    <th className="text-right px-2 py-1.5">Days Open</th>
                    <th className="text-right px-2 py-1.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.awaiting_me.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-mono text-[10px]">{r.opening_id || r.id.slice(0, 8)}</td>
                      <td className="px-2 py-1.5 font-semibold">{r.designation}</td>
                      <td className="px-2 py-1.5 text-right">{r.openings}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {r.budgeted_ctc_max
                          ? Math.round(r.budgeted_ctc_max).toLocaleString("en-IN")
                          : "—"}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase ${
                          r.priority === "high" ? "bg-red-100 text-red-700"
                          : r.priority === "low" ? "bg-slate-100 text-slate-600"
                          : "bg-amber-100 text-amber-700"}`}>{r.priority || "med"}</span>
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${
                        r.days_open > 7 ? "text-red-700 font-bold" : "text-slate-700"}`}>
                        {r.days_open}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" className="h-6 text-[10px] brand-bg brand-bg-hover text-white"
                                onClick={() => setDecisionDlg(r)}
                                data-testid={`sm-decide-${r.id}`}>
                          Decide
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-3">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mb-2">
              <Clock className="h-4 w-4 text-amber-600" /> Top 5 Aging Requisitions
            </h3>
            {(data.aging_top5 || []).length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-4">Nothing aging ✓</div>
            ) : (
              <div className="space-y-1.5">
                {data.aging_top5.map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-xs
                                              px-2 py-1.5 rounded ring-1 ring-slate-100 hover:bg-slate-50">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{r.designation}</div>
                      <div className="text-[10px] text-slate-500">
                        {r.opening_id || r.id.slice(0, 8)} · {r.status} · {r.openings} open
                      </div>
                    </div>
                    <span className={`tabular-nums font-bold ${
                      r.days_open > 14 ? "text-red-700" :
                      r.days_open > 7 ? "text-amber-700" : "text-slate-700"
                    }`}>{r.days_open}d</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mb-2">
              <Users className="h-4 w-4 text-blue-600" /> Pipeline Funnel
            </h3>
            <div className="space-y-1">
              {Object.entries(data.funnel || {}).map(([stage, count]) => {
                const max = Math.max(1, ...Object.values(data.funnel || {}));
                const pct = Math.max(2, Math.round((count / max) * 100));
                return (
                  <div key={stage} className="flex items-center gap-2 text-xs">
                    <span className="w-20 text-[11px] text-slate-600">
                      {STAGE_LABELS[stage] || stage}
                    </span>
                    <div className="flex-1 bg-slate-100 rounded h-4 relative overflow-hidden">
                      <div className="h-full brand-bg opacity-80"
                           style={{ width: `${pct}%` }} />
                      <span className="absolute right-1 top-0 text-[10px] font-mono text-slate-700 leading-4">
                        {count}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setTab && setTab("pipeline")}
                    className="text-[10px] underline text-bravura-700 mt-2"
                    data-testid="sm-view-pipeline-btn">
              View live pipeline →
            </button>
          </CardContent>
        </Card>
      </div>

      <HiringVelocityCards />

      <Dialog open={!!decisionDlg} onOpenChange={(o) => !o && setDecisionDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Requisition Decision</DialogTitle>
            <DialogDescription>
              {decisionDlg?.designation} · {decisionDlg?.openings} openings
              {decisionDlg?.budgeted_ctc_max && ` · ₹${Math.round(decisionDlg.budgeted_ctc_max).toLocaleString("en-IN")}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Decision</Label>
            <Select value={decisionForm.decision}
                    onValueChange={(v) => setDecisionForm({ ...decisionForm, decision: v })}>
              <SelectTrigger data-testid="sm-decision-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
                <SelectItem value="hold">Hold</SelectItem>
              </SelectContent>
            </Select>
            <Label className="text-xs">Remarks{decisionForm.decision === "reject" ? " *" : ""}</Label>
            <Textarea rows={3} value={decisionForm.remarks}
                       onChange={(e) => setDecisionForm({ ...decisionForm, remarks: e.target.value })}
                       data-testid="sm-decision-remarks" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecisionDlg(null)}>Cancel</Button>
            <Button onClick={decide} className="brand-bg brand-bg-hover text-white"
                    data-testid="sm-decision-submit">
              Save Decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===================== Phase 16.3 — Live Pipeline Tab ===================== */
function LivePipelineTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api.get("/recruitment/pipeline")
      .then(({ data }) => setData(data))
      .catch((e) =>
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed"))
      .finally(() => setBusy(false));
  }, []);

  if (busy && !data) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!data) return null;
  const reqs = data.requisitions || [];
  const stages = data.stages || [];

  if (reqs.length === 0) {
    return (
      <Card><CardContent className="p-6 text-center text-sm text-slate-400">
        No active approved requisitions. Pipeline shows openings with at least one candidate.
      </CardContent></Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="live-pipeline-tab">
      {reqs.map((r) => {
        const total = r.total_applications || 0;
        return (
          <Card key={r.id}>
            <CardContent className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {r.opening_id || r.id.slice(0, 8)} · {r.days_open}d old
                  </div>
                  <div className="text-sm font-bold text-slate-900">{r.designation}</div>
                  <div className="text-[11px] text-slate-500">
                    {r.openings} opening{r.openings !== 1 ? "s" : ""}
                    {r.priority && ` · ${r.priority}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold tabular-nums brand-text">{total}</div>
                  <div className="text-[10px] text-slate-500">candidates</div>
                </div>
              </div>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-1">
                {stages.map((s) => {
                  const c = r.funnel?.[s] || 0;
                  return (
                    <div key={s} className={`text-center rounded p-1 ring-1 ${
                      c > 0 ? "ring-blue-200 bg-blue-50" : "ring-slate-100 bg-slate-50"
                    }`}>
                      <div className="text-[9px] uppercase text-slate-500">{STAGE_LABELS[s] || s}</div>
                      <div className={`text-sm font-bold tabular-nums ${c > 0 ? "text-blue-700" : "text-slate-400"}`}>{c}</div>
                    </div>
                  );
                })}
              </div>
              {r.last_activity && (
                <div className="text-[10px] text-slate-400 mt-1.5">
                  Last activity: {String(r.last_activity).slice(0, 16).replace("T", " ")}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}


/* ============= Phase 16.4 — Hiring Velocity & Bottlenecks ============= */
function HiringVelocityCards() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api.get("/recruitment/velocity")
      .then(({ data }) => setData(data))
      .catch(() => { /* ignore */ })
      .finally(() => setBusy(false));
  }, []);

  if (busy && !data) return null;
  if (!data) return null;

  const s = data.summary || {};
  const trend = data.monthly_trend || [];
  const stages = (data.stage_metrics || []).filter(
    (m) => m.candidates_entered > 0 && m.stage !== "new");

  const trendFormatted = trend.map((t) => {
    const [y, m] = t.month.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return {
      ...t,
      monthLabel: d.toLocaleDateString("en-IN",
        { month: "short", year: "2-digit" }),
    };
  });

  const barColor = (avg) =>
    avg > 7 ? "#DC2626" : avg > 3 ? "#D97706" : "#059669";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3"
         data-testid="hiring-velocity-cards">
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Hiring Velocity — Last 6 Months
            </h3>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
              {s.total_hires_6mo || 0} hires
            </span>
          </div>
          {s.total_hires_6mo > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={trendFormatted}
                            margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="monthLabel" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, padding: "4px 8px" }}
                    formatter={(v, n) => [v, n === "avg_days" ? "Avg days" : n]}
                    labelStyle={{ fontWeight: "bold", fontSize: 11 }}
                  />
                  <Line type="monotone" dataKey="avg_days"
                         stroke="#AF3C3C" strokeWidth={2}
                         dot={{ fill: "#AF3C3C", r: 4 }}
                         activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                <div className="bg-emerald-50 rounded p-1.5">
                  <div className="text-[9px] uppercase text-slate-500">Avg ToH</div>
                  <div className="text-sm font-bold text-emerald-700">
                    {s.avg_time_to_hire}d
                  </div>
                </div>
                <div className="bg-blue-50 rounded p-1.5">
                  <div className="text-[9px] uppercase text-slate-500">Fastest</div>
                  <div className="text-sm font-bold text-blue-700">
                    {s.fastest_hire_days}d
                  </div>
                </div>
                <div className="bg-red-50 rounded p-1.5">
                  <div className="text-[9px] uppercase text-slate-500">Slowest</div>
                  <div className="text-sm font-bold text-red-700">
                    {s.slowest_hire_days}d
                  </div>
                </div>
              </div>
              {s.best_month && (
                <div className="text-[10px] text-slate-500 mt-2 text-center">
                  🏆 Best month: <b>{s.best_month}</b> · {s.best_month_count} hires ·
                  avg {s.best_month_avg}d
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-xs text-slate-400 py-10">
              No hires in last 6 months. Once &quot;joined&quot; stage is set on candidates, this chart will populate.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-amber-600" />
              Pipeline Bottlenecks
            </h3>
            {s.bottleneck_label && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                🔥 {s.bottleneck_label}: {s.bottleneck_avg_days}d
              </span>
            )}
          </div>
          {stages.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={Math.max(180, stages.length * 26)}>
                <BarChart data={stages} layout="vertical"
                            margin={{ top: 5, right: 30, left: 30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={70} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, padding: "4px 8px" }}
                    formatter={(v, n) => {
                      if (n === "avg_days") return [v + " days", "Avg time"];
                      return [v, n];
                    }}
                  />
                  <Bar dataKey="avg_days" radius={[0, 4, 4, 0]}>
                    {stages.map((m, i) => (
                      <Cell key={i} fill={barColor(m.avg_days)} />
                    ))}
                    <LabelList dataKey="avg_days" position="right"
                                style={{ fontSize: 10 }}
                                formatter={(v) => v + "d"} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-3 gap-1 mt-2 text-center text-[10px]">
                <div><span className="inline-block w-2 h-2 rounded-full bg-emerald-600 mr-1" />Fast (&lt;3d)</div>
                <div><span className="inline-block w-2 h-2 rounded-full bg-amber-600 mr-1" />Medium (3–7d)</div>
                <div><span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1" />Slow (&gt;7d)</div>
              </div>
              <details className="text-[10px] mt-2">
                <summary className="text-bravura-700 cursor-pointer hover:underline">
                  Show drop-off % per stage
                </summary>
                <table className="w-full mt-1.5">
                  <thead className="bg-slate-50">
                    <tr className="text-[9px] uppercase text-slate-500">
                      <th className="text-left px-1.5 py-1">Stage</th>
                      <th className="text-right px-1.5 py-1">In</th>
                      <th className="text-right px-1.5 py-1">Out</th>
                      <th className="text-right px-1.5 py-1">Drop %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stages.map((m) => (
                      <tr key={m.stage} className="border-t border-slate-100">
                        <td className="px-1.5 py-1">{m.label}</td>
                        <td className="px-1.5 py-1 text-right">{m.candidates_entered}</td>
                        <td className="px-1.5 py-1 text-right">{m.candidates_advanced}</td>
                        <td className={`px-1.5 py-1 text-right font-semibold ${
                          m.drop_off_pct > 50 ? "text-red-700"
                            : m.drop_off_pct > 25 ? "text-amber-700" : "text-emerald-700"
                        }`}>{m.drop_off_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </>
          ) : (
            <div className="text-center text-xs text-slate-400 py-10">
              Not enough pipeline movement in last 6 months yet.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
